<script>
    // Packages
    import {ref, reactive} from 'vue';
    import { useForm } from '@inertiajs/vue3';
    import {Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue';

    // Icons
    import {XIcon} from '@heroicons/vue/outline';
    import {LinkIcon, PlusSmIcon, QuestionMarkCircleIcon, ExclamationCircleIcon} from '@heroicons/vue/solid';

    // Components
    import SlideOutErrors from "@/Components/SlideOutErrors.vue";

    export default {
        components: {
            XIcon,
            Dialog,
            LinkIcon,
            PlusSmIcon,
            DialogTitle,
            DialogOverlay,
            SlideOutErrors,
            TransitionRoot,
            TransitionChild,
            ExclamationCircleIcon,
            QuestionMarkCircleIcon
        },
        setup(prop, context) {

            const show = ref(false)

            const showErrors = ref(false)

            const updating = false;

            const form = useForm({
                id: null,
                bank: null,
                branch_address: null,
                account_name: null,
                sort_code: null,
                account_number: null,
                bic: null,
                swift: null,
                iban: null,
                routing: null
            })

            function submit() {
                if(this.updating) {
                    this.form.post(route('admin.settings.bank-details.bank-detail.update', this.form.id), {
                        onSuccess: page => {
                            this.show = false;
                            this.showErrors = false;
                            this.form.reset();
                        },
                        onError: errors => {
                            this.showErrors = true;
                        }
                    });
                } else {
                    this.form.post(route('admin.settings.bank-details.create'), {
                        onSuccess: page => {
                            this.show = false;
                            this.showErrors = false;
                            this.form.reset();
                        },
                        onError: errors => {
                            this.showErrors = true;
                        }
                    });
                }
            }

            function close() {
                this.form.reset();
                this.form.clearErrors();
                this.show = false;
            }

            function open() {
                this.form.clearErrors();
                this.show = true;
            }

            return {
                show,
                form,
                open,
                close,
                submit,
                updating,
                showErrors
            }
        }
    }
</script>

<template>
    <TransitionRoot as="template" :show="show">
        <Dialog as="div" class="fixed inset-0 overflow-hidden" @close="close()">
            <div class="absolute inset-0 overflow-hidden">
                <DialogOverlay class="absolute inset-0 bg-gray-500 opacity-75"/>
                <div class="fixed inset-y-0 pl-16 max-w-full right-0 flex">
                    <TransitionChild as="template" enter="transform transition ease-in-out duration-500 sm:duration-700"
                        enter-from="translate-x-full" enter-to="translate-x-0"
                        leave="transform transition ease-in-out duration-500 sm:duration-700"
                        leave-from="translate-x-0" leave-to="translate-x-full">
                        <div class="w-screen max-w-lg">
                            <form class="h-full divide-y divide-gray-200 flex flex-col bg-white shadow-xl" @submit.prevent="submit">
                                <div class="flex-1 h-0 overflow-y-auto">
                                    <div class="py-6 px-4 bg-dark-pitch-purple sm:px-6">
                                        <div class="flex items-center justify-between">
                                            <DialogTitle class="text-lg text-white font-bold" v-if="form.id">
                                                Update Bank Details - {{ form.bank }}
                                            </DialogTitle>
                                            <DialogTitle class="text-lg text-white font-bold" v-else>
                                                Create Bank Details
                                            </DialogTitle>
                                            <div class="ml-3 h-7 flex items-center">
                                                <button type="button"
                                                    class="bg-dark-pitch-purple rounded-md text-indigo-200 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
                                                    @click="close()">
                                                    <span class="sr-only">Close panel</span>
                                                    <XIcon class="h-6 w-6" aria-hidden="true"/>
                                                </button>
                                            </div>
                                        </div>
                                        <div class="mt-1">
                                            <p class="text-sm text-white">
                                                <span v-if="form.id">
                                                    Fill in the required information below to update these bank details.
                                                </span>
                                                <span v-else>
                                                    Get started by filling in the information below to create new bank details.
                                                </span>
                                            </p>
                                        </div>
                                    </div>
                                    <SlideOutErrors :show="showErrors === true"/>
                                    <div class="flex-1 flex flex-col justify-between">
                                        <div class="px-4 divide-y divide-gray-200 sm:px-6">
                                            <div class="space-y-6 pt-6 pb-5">
                                                <div>
                                                    <label for="bank" class="block text-sm text-gray-900 required">
                                                        Bank
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" v-model="form.bank" id="bank" :class="{'border-red-500': form.errors.bank}"
                                                            class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="branch_address" class="block text-sm text-gray-900">
                                                        Branch Address
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" v-model="form.branch_address" id="branch_address" :class="{'border-red-500': form.errors.branch_address}"
                                                            class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="account_name" class="block text-sm text-gray-900 required">
                                                        Account Name
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" v-model="form.account_name" id="account_name" :class="{'border-red-500': form.errors.account_name}"
                                                            class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="sort_code" class="block text-sm text-gray-900 required">
                                                        Sort Code
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" v-model="form.sort_code" id="sort_code" :class="{'border-red-500': form.errors.sort_code}"
                                                            class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="account_number" class="block text-sm text-gray-900 required">
                                                        Account Number
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" v-model="form.account_number" id="account_number" :class="{'border-red-500': form.errors.account_number}"
                                                            class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="bic" class="block text-sm text-gray-900">
                                                        BIC
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" v-model="form.bic" id="bic" :class="{'border-red-500': form.errors.bic}"
                                                            class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="swift" class="block text-sm text-gray-900">
                                                       Swift 
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" v-model="form.swift" id="swift" :class="{'border-red-500': form.errors.swift}"
                                                            class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="iban" class="block text-sm text-gray-900">
                                                        IBAN
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" v-model="form.iban" id="iban" :class="{'border-red-500': form.errors.iban}"
                                                            class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label for="routing" class="block text-sm text-gray-900">
                                                        Routing
                                                    </label>
                                                    <div class="mt-1">
                                                        <input type="text" v-model="form.routing" id="iban" :class="{'border-red-500': form.errors.routing}"
                                                            class="block w-full shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-light-pitch-purple border-gray-300 rounded-md"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="flex-shrink-0 px-4 py-4 flex justify-end">
                                    <button type="button"
                                        class="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                        @click="close()">
                                        Close
                                    </button>
                                    <button :disabled="form.processing || !form.isDirty" type="submit"
                                        class=" ml-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm rounded-md text-white bg-dark-pitch-purple hover:bg-dark-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-gray-200">
                                        <span v-if="updating === true">Update</span>
                                        <span v-else>Save</span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>